import { Md5 } from 'ts-md5';

//-----------------------------------------------------------------------------------------
// Type Definitions - General:
//-----------------------------------------------------------------------------------------

export interface Option {
	id: string;
	name: string;
	currencyCode: string;
	price: number;
	optionIsStandard: boolean;
	description: string;
}

export type Options = Record<string, Option>;

export type LeadInputValues = {
	firstName: string;
	lastName: string;
	email: string;
	address1?: string;
	address2?: string;
	address3?: string;
	address4?: string;
	postcode?: string;
	country?: string;
	tel?: string;
};

// the response returned by the ipinfo lite API, used for automatic region detection (n.b. only the country code is used).
export interface IpInfoResponse {
	ip: string;
	asn: string;
	as_name: string;
	as_domain: string;
	country_code: string;
	country: string;
	continent_code: string;
	continent: string;
}

//-----------------------------------------------------------------------------------------
// Type Definitions - JSON classes:
//-----------------------------------------------------------------------------------------

export interface JsonConfiguration {
	options: JsonConfigurationOptions;
	summary: Summary[];
}

export interface JsonConfigurationOptions {
	model: Model;
	paintwork: Paintwork;
	wheels: Wheels;
	interior_textiles: InteriorTextiles;
	cockpit: Cockpit;
	exterior_textiles: ExteriorTextiles;
	final_finishes: FinalFinishes;
	accessories: Accessories;
}

export interface Model {
	type: Item;
	gearbox: Item;
}

export interface Paintwork {
	colourways: Colourways;
}

export interface Colourways {
	two_tone: Item;
	paint_body: PaintItem;
	paint_wings: PaintItem;
}

export interface Wheels {
	style: Item;
	wheel_nuts: Item;
	spare_wheel: Item;
}

export interface InteriorTextiles {
	leather: Leather;
	seats: Seats;
	piping: Item;
	stitching: Item;
	carpet: Item;
}

export interface Leather {
	style: Item;
	trim: Item;
}

export interface Seats {
	pleating: Item;
	seat_back: Item;
	headrest_embroidery: Item;
	comfort_seats: Item;
	heated_seats: Item;
}

export interface Cockpit {
	dashboard_face: Item;
	dashboard_lower_rail: PaintItem;
	center_column: PaintItem;
	steering_wheel_centre: Item;
	leather_details: Item[];
	convenience: Item[];
}

export interface ExteriorTextiles {
	hood: Item;
	wing_beading: Item;
}

export interface FinalFinishes {
	cowl: Item[];
	callsigns: (Item & { pilot: string; 'co-pilot'?: string; copilot?: string })[];
	graphics: Item[];
	accents: Item[];
	splitter: Item;
	badges: Item;
	paint_protection_film: Item;
}

export interface Accessories {
	storage_bags: Item[];
	covers: Item[];
	equipment: Item[];
	services: Item[];
	aftermarket: Item[];
}

export interface PaintItem {
	id: string;
	lacquer_matt: boolean;
	name: string;
	price: string;
	lacquer_price: string;

	// the chosen colour as '#rrggbb', sent only for a bespoke paint option, whose ID gives the finish but not the colour.
	// optional, as every standard colour omits it and as configurations saved before it existed do not carry it.
	option_hex?: string;
}

export interface Item {
	id: string;
	name: string;
	price: string;
}

export interface Summary {
	group: string;
	items: SummaryItem[];
}

export interface SummaryItem {
	name: string;
	option_code: string;
	value: string;
	price: string;
}

//-----------------------------------------------------------------------------------------
// Constants:
//-----------------------------------------------------------------------------------------

const MD5_SECRET_KEY = '2vxB82739K67gVF';
const WEB_API_URL = 'https://webapi.morgan-motor.com';
const GET_UI_TEXT_ENDPOINT = 'getuitext.aspx';
const GET_NAMED_OPTIONS_ENDPOINT = 'getnamedoptions.aspx';
const LOAD_CONFIGURATION_ENDPOINT = 'loadconfiguration.aspx';
const SAVE_CONFIGURATION_ENDPOINT = 'saveconfiguration.aspx';
const SUBMIT_LEAD_ENDPOINT = 'SubmitLead.aspx';
const GENERATE_PDF_ENDPOINT = 'createpdf.aspx';
const SAVE_IMAGES_ENDPOINT = 'saveimages.aspx';

// ipinfo lite API, used to automatically detect the user's country/region.
const IPINFO_API_URL = 'https://api.ipinfo.io/lite/me';
const IPINFO_TOKEN = 'f3df7a3871028f';

/**
 * Web Service:
 */
export abstract class WebService {

	//-----------------------------------------------------------------------------------------
	// Public Functions:
	//-----------------------------------------------------------------------------------------
	
	static async getOptions(tableName: string, modelId: string, regionCode: string, languageCode: string): Promise<Options> {

		// compile the parameters to be sent.
		const formData = new FormData();
		formData.append('modelId', modelId);
		formData.append('regionCode', regionCode);
		formData.append('languageCode', languageCode);
		formData.append('tableName', tableName);

		// calculate the hash.
		formData.append('hash', Md5.hashStr(modelId + regionCode + languageCode + MD5_SECRET_KEY));

		// post the parameters to the relevant web service endpoint, and await the response.
		const url = `${WEB_API_URL}/${GET_NAMED_OPTIONS_ENDPOINT}`;
		const response = await fetch(url, { body: formData, method: 'post' });

		// ensure a success status is received.
		if (response.status !== 200) {
			throw new Error('getOptions returned non-success status');
		}

		// get the response.
		const json = await response.json();

		// convert the JSON result into a record of options with the option ID as the key.
		const results: Options = {};
		for (const item of json) {

			// create the record.
			const record: Option = {
				id: item.optionIdUnique,
				name: item.optionName,
				currencyCode: item.currencyCode,
				price: item.price,
				optionIsStandard: item.optionIsStandard !== '0',
				description: item.description
			};

			// add the record, specifying the key.
			results[item.optionIdUnique] = record;
		}

		// return the results.
		return results;
	}

	static async loadConfiguration(shortCode: string): Promise<JsonConfiguration> {

		// compile the parameters to be sent.
		const formData = new FormData();
		formData.append('shortCode', shortCode);

		// calculate the hash.
		formData.append('hash', Md5.hashStr(shortCode + MD5_SECRET_KEY));

		// post the parameters to the relevant web service endpoint, and await the response.
		const url = `${WEB_API_URL}/${LOAD_CONFIGURATION_ENDPOINT}`;
		const response = await fetch(url, { body: formData, method: 'post' });

		// ensure a success status is received.
		if (response.status !== 200) {
			throw new Error('loadConfiguration returned non-success status');
		}

		// get the response.
		const json = await response.json();

		// the long code contains a base64-encoded string containing the config info.
		const base64String = json['longcode'];

		// convert the base-64-encoded string to JSON.
		const jsonConfig = atob(base64String);

		// de-serialise the JSON as an object, and return it.
		return JSON.parse(jsonConfig);
	}

	static async saveConfiguration(regionCode: string, configuration: object): Promise<{ shortcode: string }> {

		// convert the configuration object to a JSON string, escaping any characters that btoa
		// cannot encode, and then base64-encode it.
		const jsonBody = escapeNonLatin1Characters(JSON.stringify(configuration));
		const longCode = btoa(jsonBody);

		// generate timestamp in the specific format needed to generate the hash.
		const timestamp = getCustomTimestamp();

		// create the payload for the request.
		const requestPayload = {
			regionCode,
			longcode: longCode,
			timestamp,
			hash: Md5.hashStr(
				regionCode +
				timestamp +
				MD5_SECRET_KEY)
		};

		// post the parameters to the relevant web service endpoint, and await the response.
		const url = `${WEB_API_URL}/${SAVE_CONFIGURATION_ENDPOINT}`;
		const response = await fetch(url, { body: JSON.stringify(requestPayload), method: 'post' });

		// ensure a success status is received.
		if (response.status !== 200) {
			throw new Error('saveConfiguration returned non-success status');
		}

		// get the response.
		const json = await response.json();

		// return the json object.
		return json;
	}
	
	// needs bringing up to speed - see above!!
	static getUiText(languageCode: string): void {
		
		// compile the parameters to be sent.
		const formData = new FormData();
		formData.append('languageCode', languageCode);

		// calculate the hash.
		formData.append('hash',  Md5.hashStr(languageCode + MD5_SECRET_KEY));

		// post the parameters to the relevant web service endpoint.
		const url = `${WEB_API_URL}/${GET_UI_TEXT_ENDPOINT}`;
		fetch(url, { body: formData, method: 'post' })
			.then(response => response.json())
			.then(response => {
				console.log(response);
			});
	}

	static async sendToDealer(inputValues: LeadInputValues, shortCode: string, selectedDealerId: string, leadValue: string, value: number, hasUserConsent: boolean, hasMarketingOptin: boolean): Promise<Response> {
		const model = 'Plus Four';

		// create FormData for the request.
		const formData = new FormData();

		// the lead endpoint rejects blank names, so fall back to a placeholder when they are empty.
		// these normalised values must feed both the form fields and the hash below, or the hash will not match.
		const firstName = inputValues?.firstName || 'N/A';
		const lastName = inputValues?.lastName || 'N/A';

		formData.append('formLabel', 'configurator_plusfour');
		formData.append('leadValue', leadValue);
		formData.append('value', value.toString());
		formData.append('firstName', firstName);
		formData.append('lastName', lastName);
		formData.append('telephone', inputValues?.tel || '');
		formData.append('email', inputValues?.email);
		formData.append('streetAddress', inputValues?.address1 || '');
		formData.append('addressLine', inputValues?.address2 || '');
		formData.append('city', inputValues?.address3 || '');
		formData.append('countryState', inputValues?.address4 || '');
		formData.append('country', inputValues?.country || '');
		formData.append('postalCode', inputValues?.postcode || '');
		formData.append('dealerId', selectedDealerId || '');
		formData.append('model', model);
		formData.append('hasUserConsent', `${hasUserConsent}`);
		formData.append('hasMarketingOptin', `${hasMarketingOptin}`);
		formData.append('shortcode', shortCode);
		formData.append('hash', Md5.hashStr(firstName + lastName + inputValues.email + MD5_SECRET_KEY));

		// post the parameters to the relevant web service endpoint, and await the response.
		const url = `${WEB_API_URL}/${SUBMIT_LEAD_ENDPOINT}`;
		const response = await fetch(url, { body: formData, method: 'post' });

		// ensure a success status is received.
		if (response.status !== 200) {
			throw new Error('sendToDealer returned non-success status');
		}

		// optionally log the form data for debugging!!
		//for (const [key, val] of formData.entries()) {
		//	console.log(`${key}: ${val}`);
		//}

		// return the json object.
		return response;
	}

	static async generatePdf(regionCode: string, languageCode: string, shortCode: string, configuration: object, imageUrls: string[], email: string, firstName: string, lastName: string,
		sendEmailTemplate?: string, dealerName?: string, dealerCompanyName?: string, dealerEmail?: string): Promise<{ pdfUrl: string }> {

		// generate timestamp in the specific format needed to generate the hash.
		const timestamp = getCustomTimestamp();

		// create the payload for the request.
		const requestPayload = {
			...configuration,
			shortcode: shortCode,
			model: 'plusfour',
			region: regionCode,
			language: languageCode,
			images: imageUrls,
			sendEmail: email,
			clientFirstName: firstName,
			clientLastName: lastName,
			...(sendEmailTemplate && { sendEmailTemplate }),
			...(dealerName && { dealerName }),
			...(dealerCompanyName && { dealerCompanyName }),
			...(dealerEmail && { dealerEmail }),
			timestamp,
			hash: Md5.hashStr(
				regionCode +
				timestamp +
				MD5_SECRET_KEY),
		};

		// post the parameters to the relevant web service endpoint, and await the response.
		const url = `${WEB_API_URL}/${GENERATE_PDF_ENDPOINT}`;
		const response = await fetch(url, { body: JSON.stringify(requestPayload), method: 'post' });

		// ensure a success status is received.
		if (response.status !== 200) {
			throw new Error('saveConfiguration returned non-success status');
		}

		// get the response.
		const json = await response.json();

		// log the request and response payloads for debugging!!
		//console.log(requestPayload);
		//console.log(json);

		// return the json object.
		return json;
	}

	static async saveImages(images: Blob[]): Promise<{ [key: string]: string }[]> {

		// generate timestamp in the specific format needed to generate the hash.
		const timestamp = getCustomTimestamp();

		// create the hash using the timestamp and the secret key.
		const hash = Md5.hashStr(timestamp + MD5_SECRET_KEY);

		// setup form data.
		const formData = new FormData();
		formData.append('timestamp', timestamp);
		formData.append('hash', hash);

		// add each image to form data.
		for (const [index, image] of images.entries()) {
			formData.append(`image${index}`, image);
		}

		try {

			// post the parameters to the relevant web service endpoint, and await the response.
			const url = `${WEB_API_URL}/${SAVE_IMAGES_ENDPOINT}`;
			const response = await fetch(url,
				{
					body: formData,
					method: 'post',
				});

			// ensure a success status is received.
			if (response.status !== 200) {
				throw new Error('saveImages returned non-success status');
			}

			// get the response.
			const json = await response.json();

			return json;
		} catch (err) {
			console.error(err);
			return [];
		}
	}

	// detect the user's region by looking up their IP via the ipinfo lite API, returning the lowercase country code (or null on failure).
	static async detectRegionCode(): Promise<string | null> {
		try {

			// look up the user's IP info from the relevant web service endpoint, and await the response.
			const url = `${IPINFO_API_URL}?token=${IPINFO_TOKEN}`;
			const response = await fetch(url);

			// ensure a success status is received.
			if (response.status !== 200) {
				throw new Error('detectRegionCode returned non-success status');
			}

			// get the response, and return the country code as a lowercase region code (or null if not present).
			const json = await response.json() as IpInfoResponse;
			return json.country_code?.toLowerCase() ?? null;
		} catch (err) {
			console.error(err);
			return null;
		}
	}
}

//-----------------------------------------------------------------------------------------
// Private Functions:
//-----------------------------------------------------------------------------------------

/**
 * Rewrites every character outside the Latin-1 range as a JSON \uXXXX escape sequence, leaving the
 * rest of the string untouched. btoa throws on anything above U+00FF, which the region-specific
 * currency formatting routinely produces - the euro sign (U+20AC) for the eurozone regions, the
 * Polish zloty (U+0142), the Swiss thousands apostrophe (U+2019) and the Japanese yen (U+FFE5). The
 * escapes are resolved back to the original characters by JSON.parse, so a configuration decoded
 * from the resulting long code is unchanged.
 */
function escapeNonLatin1Characters(json: string): string {

	// replace each out-of-range character with its escape sequence.
	return json.replace(/[\u0100-\uffff]/g, character =>
		`\\u${character.charCodeAt(0).toString(16).padStart(4, '0')}`);
}

// generate timestamp in the specific format needed to generate the hash.
function getCustomTimestamp() {
	const now = new Date();
	const year = now.getFullYear();
	const month = String(now.getMonth() + 1).padStart(2, '0'); // Month is zero-based
	const day = String(now.getDate()).padStart(2, '0');
	const hours = String(now.getHours()).padStart(2, '0');
	const minutes = String(now.getMinutes()).padStart(2, '0');
	const seconds = String(now.getSeconds()).padStart(2, '0');
	const milliseconds = String(now.getMilliseconds()).padStart(3, '0');
	return `${year}${month}${day}${hours}${minutes}${seconds}${milliseconds}`;
}
